"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { parseMarkdownToHtml } from "@/lib/markdown";
import { COMPANY_INFO } from "@/lib/company";
import { formatDate } from "@/lib/datetime";

interface LegalDoc {
  key: string;
  title: string;
  content: string;
  updated_at: string;
}

export default function TermsOfServicePage() {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/legal/terms_of_service")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load terms of service.");
        return res.json();
      })
      .then((data) => {
        setDoc(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const renderedHtml = doc ? parseMarkdownToHtml(doc.content) : "";
  const lastUpdated = doc
    ? formatDate(doc.updated_at)
    : "Loading...";

  return (
    <div className="bg-canvas min-h-screen py-16 px-6 md:px-8 relative overflow-hidden">
      {/* 🔮 Background Light Ray Glow */}
      <div className="absolute top-0 right-1/4 h-[400px] w-[400px] rounded-full bg-blue-500/5 blur-[100px]" />
      
      <div className="max-w-3xl mx-auto relative z-10 space-y-8">
        <div className="border-b border-divider pb-6">
          <Link href="/" className="text-xs font-mono tracking-wider text-brand-primary uppercase hover:underline">
            ← Return to Storefront
          </Link>
          <h1 className="text-3xl font-black text-primary tracking-tight mt-3">
            {doc?.title || "Terms of Service"}
          </h1>
          <p className="text-xs text-secondary mt-1 font-mono">
            Last updated: {lastUpdated}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-3">
            <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent animate-spin rounded-full" />
            <p className="text-xs text-secondary font-mono">Retrieving active policy guidelines...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
            <p className="text-sm text-red-450 font-mono">Error loading Terms of Service: {error}</p>
          </div>
        ) : (
          <div
            className="space-y-6 text-sm text-secondary leading-relaxed font-sans prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}

        {/* Company Registration Details */}
        <div className="rounded-card bg-surface-inset p-6 space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-primary">Company Registration Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <div className="flex justify-between gap-4 border-b border-edge-subtle pb-2">
              <dt className="text-secondary">Legal Entity</dt>
              <dd className="font-semibold text-primary text-right">{COMPANY_INFO.legalName}</dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-edge-subtle pb-2">
              <dt className="text-secondary">VAT Number</dt>
              <dd className="font-mono font-semibold text-primary text-right">{COMPANY_INFO.vatNumber}</dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-edge-subtle pb-2">
              <dt className="text-secondary">KVK Number</dt>
              <dd className="font-mono text-primary text-right">{COMPANY_INFO.kvkNumber}</dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-edge-subtle pb-2">
              <dt className="text-secondary">Registered Address</dt>
              <dd className="text-primary text-right">{COMPANY_INFO.address}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}